import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { post } from "@/lib/api";
import { cancelText } from "@/lib/documents";
import type { Approval, Document, DocumentDetail } from "@/lib/types";
import { FINALIZE_DISABLED } from "../../../invoicing/constants.ts";
import { ApprovalFooter, ApprovalNoticeLine, askedAt } from "./ApprovalBanner";
import { useApprovalDecision, useRefreshDocument } from "./useDocument";

type CancelResult = { original: Document; cancellation: Document };

/** „RE-1086 stornieren?“; with `approval` the agent's request to approve or reject. */
export function CancelDialog({ detail, approval, onClose }: { detail: DocumentDetail; approval?: Approval; onClose: () => void }) {
  const doc = detail.document;
  const refresh = useRefreshDocument(doc.id);
  const decision = useApprovalDecision({ id: approval?.id ?? 0, documentId: doc.id });
  const done = (r: CancelResult | null) => {
    onClose();
    toast.success(r?.cancellation.number ? `${doc.number} storniert – Stornorechnung ${r.cancellation.number}` : `${doc.number} storniert`);
  };
  const cancel = useMutation({
    mutationFn: () => post<{ status: "done"; result: CancelResult } | { status: "pending" }>(`/api/documents/${doc.id}/cancel`),
    onSuccess: (r) => {
      if (r.status === "pending") {
        onClose();
        toast.info("Wartet auf Freigabe");
        return;
      }
      done(r.result);
    },
    onSettled: refresh,
  });
  const off = !detail.finalizeEnabled;
  const error = cancel.error?.message ?? decision.approve.error?.message ?? null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-6 text-left">
          {approval && <p className="text-[0.8125rem] text-muted-foreground">{approval.title} · {askedAt(approval.requestedAt)}</p>}
          <DialogTitle>{doc.number} stornieren?</DialogTitle>
          <DialogDescription className="text-pretty">{cancelText(doc)}</DialogDescription>
        </DialogHeader>
        {approval && <ApprovalNoticeLine approval={approval} number={doc.number} />}
        {off && <p role="status" className="text-[0.8125rem] text-pretty text-muted-foreground">{FINALIZE_DISABLED}</p>}
        {error && <p role="alert" className="text-[0.8125rem] text-destructive">{error}</p>}
        {approval ? (
          <ApprovalFooter
            approval={approval}
            primary="Freigeben & stornieren"
            disabled={off || decision.approve.isPending}
            onApprove={() => decision.approve.mutate({}, { onSuccess: (a) => done(a.result as CancelResult | null) })}
            onClose={onClose}
          />
        ) : (
          <DialogFooter>
            <Button variant="ghost" className="pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
            <Button variant="destructive" className="pointer-coarse:h-11" disabled={off || cancel.isPending} onClick={() => cancel.mutate()}>
              Stornieren
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
